import { Injectable } from '@angular/core';
import {
  DailyLogEditHelper,
  DailyLogOperationInstance,
  DailyLogType,
  DailyOperationLogRecord,
  StepInstance
} from '../../../models/projects/operation.models';
import { DateService } from '../../date/date.service';
import { DailyLogService } from '../daily-log.service';
import { OperationService } from '../../operations/operation/operation.service';
import { DateParserService } from './date-parser-service';
import { RecordOperationLinkService } from '../../record-operation-link/record-operation-link.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { cloneDeep } from 'lodash';
import { SetupService } from '../../setup/setup.service';
import { interval } from 'rxjs/index';
import { DateChangeRecordService } from '../date-change-record/date-change-record.service';
import { DailyLogComponent } from '../../../pages/daily-log/daily-log-component/daily-log-component.component';
import { AppData } from '../../../models/app-data/app-data';
import { AppDataSyncService } from '../../app-data-sync/app-data-sync.service';
import { TimeZoneService } from '../../time-zone/time-zone.service';
import { OperationTimeService } from '../../operations/operation-time/operation-time.service';
import { TimeZoneChangeEditor } from '../../../models/time-zones/time-zone.models';
import { DateTime } from 'luxon';

@Injectable()
export class DailyLogTimeService {

  startChanged: boolean = false;
  endChanged: boolean = false;
  recordsUpdated: boolean = false;
  endTimeRemoved: boolean = false;
  appData: AppData;
  preventDataChange: boolean = false;

  lastTimeConfirmedFlag: boolean = false;
  timeConfirmedMouseUpFlag: boolean = false;
  mouseUpSubscribe: any;

  constructor(
    public dateService: DateService,
    public dailyLogService: DailyLogService,
    public operationService: OperationService,
    public dateParserService: DateParserService,
    public recordOperationLinkService: RecordOperationLinkService,
    public setupService: SetupService,
    private dateChangeRecordService: DateChangeRecordService,
    private appDataSyncService: AppDataSyncService,
    private timeZoneService: TimeZoneService,
    private operationTimeService: OperationTimeService
  ) {
  }

  async startTimeChanged(dailyLogData: DailyOperationLogRecord[], changedRecordId: number, dailyLogDate: PtLuxonDate): Promise<DailyLogEditHelper> {
    const dataCopy = cloneDeep(dailyLogData) as DailyOperationLogRecord[];
    const changedIndex = dailyLogData.findIndex(e => changedRecordId > 0 ? e.id === changedRecordId : e.localId === changedRecordId);
    const changedRecord = cloneDeep(dailyLogData[changedIndex]) as DailyOperationLogRecord;
    this.resetVariables();
    this.startChanged = true;

    // set startConfirmed to false here so it doesn't interfere with logic when calculating times later, will be changed back at the end
    if (changedRecord.typeId === 1 || changedRecord.typeId === 2 || changedRecord.typeId === 3) {
      changedRecord.startTimeConfirmed = false;
    }

    // set time for the changed record here
    if (!changedRecord.stepInstance) {
      dailyLogData[changedIndex].startTime = await this.dateService
        .setPtLuxonDateFromUserEnteredString(dailyLogData[changedIndex].startTimeShow, dailyLogData[changedIndex].startTime.dateTime);

      // if op start time is changed, all records that share the same operation need to update their operationStartTime
      const updatedOperationRecords = this.updateOperationStartTimes(dailyLogData, changedRecord.operationInstanceId
        ? changedRecord.operationInstanceId
        : changedRecord.operationInstanceLocalId, dailyLogData[changedIndex].startTime);
      changedRecord.operationStartTime = dailyLogData[changedIndex].startTime; // also update this record - it will get used later
      this.appData.dailyLogRecords.concat(updatedOperationRecords);
    }

    switch (changedRecord.typeId) {
      case 1:
      case 2:
      case 3:
        if ((await this.timeChanged(changedRecord.startTime.dateTime, dailyLogData[changedIndex].startTimeShow))) {
          dailyLogData = await this.updateFutureRecords(dailyLogData, changedRecord, dailyLogDate);

          if (this.preventDataChange) {
            return {preventDataChange: this.preventDataChange, dailyLogData: dataCopy};
          }
        }

        /* ToDO - this is causing operations to be saved twice when the user edits an end time in the daily log, and the second time
          doesn't save the correct times any more after the latest timezone changes were made. I don't know if this is actually needed,
          so commenting it out but will leave it here in case anything breaks. */
        // this.mouseUpWait(dailyLogData[changedIndex], true, component);
        //   false,
        //   component,
        //   dailyLogData[changedIndex].endTimeConfirmed);

        // update operation times so the time's are correct in the edit page
        await this.updateRecordOperation(this.appData.dailyLogRecords);

        break;
    }


    if (changedRecord.typeId === DailyLogType.OperationStart && changedRecord.operationClassification === 'Rig Move') {
      await this.updateTransitingRecord(changedRecord, dailyLogData);
    }

    if (changedRecord.stepInstance) {
      if (changedRecord.operationInstanceId || changedRecord.operationInstanceLocalId) {
        let operation = await this.operationService.getProjectSpecificOperationInstance(changedRecord.operationInstanceId || changedRecord.operationInstanceLocalId);

        if (operation) {
          dailyLogData = this.updateOperationStartTimes(dailyLogData, changedRecord.operationInstanceId
            ? changedRecord.operationInstanceId
            : changedRecord.operationInstanceLocalId, operation.startTime);
        }
      }
    } else {
      const newStartTime = cloneDeep(changedRecord.startTime);
      dailyLogData = this.updateOperationStartTimes(dailyLogData, changedRecord.operationInstanceId
        ? changedRecord.operationInstanceId
        : changedRecord.operationInstanceLocalId, newStartTime);
    }

    // clone each operationStartTime to fix bug where next call to addSystemOffsetsToDailyLogRecords causes all operationStartTimes to get
    // added together cummutively in each loop due to some assign by reference shenanigans
    this.appData.dailyLogRecords.forEach(e => {
      e.operationStartTime = cloneDeep(e.operationStartTime);
    });


    const appDataClone = cloneDeep(this.appData) as AppData;

    appDataClone.dailyLogOperationInstances.forEach(operationInstance => {
      this.updateOperationEndTime(operationInstance, appDataClone.dailyLogRecords);
    });

    await this.appDataSyncService.syncLocalData(appDataClone, true, null, true);

    // not all operation records are in the appData.dailyLogRecords, and any that aren't will still have the operationStartTime in UTC,
    // so we need to update them. operationStartTime should be correct local time on changedRecord here
    if (changedRecord.operationStartTime) {
      dailyLogData = this.updateOperationStartTimes(dailyLogData, changedRecord.operationInstanceId
        ? changedRecord.operationInstanceId
        : changedRecord.operationInstanceLocalId, changedRecord.operationStartTime);
    }

    return {preventDataChange: this.preventDataChange, dailyLogData: dailyLogData};
  }

  async endTimeChanged(
    dailyLogData: DailyOperationLogRecord[],
    changedRecordId: number,
    dailyLogDate: PtLuxonDate
  ): Promise<DailyLogEditHelper> {
    const dataCopy = cloneDeep(dailyLogData);
    const changedIndex = dailyLogData.findIndex(e => changedRecordId > 0 ? e.id === changedRecordId : e.localId === changedRecordId);
    const changedRecord = cloneDeep(dailyLogData[changedIndex]) as DailyOperationLogRecord;
    this.resetVariables();
    this.endChanged = true;
    const canConfirmTimes = await this.canConfirmTimes(changedRecord);

    // set endConfirmed to false here so it doesn't interfere with logic when calculating times later, will be changed back at the end
    if (canConfirmTimes && (changedRecord.typeId === 1 || changedRecord.typeId === 2 || changedRecord.typeId === 3)) {
      changedRecord.endTimeConfirmed = false;
    }

    if (!changedRecord.stepInstance) {
      dailyLogData[changedIndex].endTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
        dailyLogData[changedIndex].endTimeShow,
        dailyLogData[changedIndex].endTime.dateTime);

      // await this.updateRecordOperation([dailyLogData[changedIndex]]);
      this.appData.dailyLogRecords.push(dailyLogData[changedIndex]);
    }

    // need to set the operation start time here to keep it in line with the other record times, even though we are not editing the operations start time itself
    // if we don't do this, then the record.operationStartTime will be saved with the wrong time in the db (it will be local not UTC)
    if (changedRecord.operationStartTime && changedRecord.operationStartTime.dateTime) {
      const updatedOperationRecords = this.updateOperationStartTimes(dailyLogData, changedRecord.operationInstanceId
        ? changedRecord.operationInstanceId
        : changedRecord.operationInstanceLocalId, changedRecord.operationStartTime);
      this.appData.dailyLogRecords.concat(updatedOperationRecords);
    }

    switch (changedRecord.typeId) {
      case 1:
      case 2:
        if ((await this.timeChanged(changedRecord.endTime.dateTime, dailyLogData[changedIndex].endTimeShow))) {
          dailyLogData = await this.updateFutureRecords(dailyLogData, changedRecord, dailyLogDate);

          if (this.preventDataChange) {
            return {preventDataChange: this.preventDataChange, dailyLogData: dataCopy};
          }
        }

        if (canConfirmTimes) {
          dailyLogData[changedIndex].endTimeConfirmed = dailyLogData[changedIndex].endTime != null ? true : null;
        }

        // commenting this out - was causing an issue where the api call to save is called twice. This was originally put in to stop the end time from
        // being confirmed and then immediately unconfirmed, if the user had their caret in the input, and then clicked the endConfirmed checkbox
        // this.mouseUpWait(dailyLogData[changedIndex], false, component, dailyLogData[changedIndex].endTimeConfirmed);

        await this.updateRecordOperation(this.appData.dailyLogRecords);

        break;
      case 3:
        if ((await this.timeChanged(changedRecord.endTime.dateTime, dailyLogData[changedIndex].endTimeShow))) {
          // case 3 is operation end time so no future records to update, but still need to update operation as well as record sort time
          if (changedRecord.operationClassification === 'Rig Move') {
            await this.updateLocationChangeRecord(changedRecord.operationInstanceId, dailyLogData);
          }
          await this.updateRecordOperation(this.appData.dailyLogRecords);
          dailyLogData[changedIndex].sortTime = dailyLogData[changedIndex].endTime.dateTime;

          if (this.preventDataChange) {
            return {preventDataChange: this.preventDataChange, dailyLogData: dataCopy};
          }
        }

        if (!dailyLogData[changedIndex].endTimeShow) {
          this.endTimeRemoved = true;
          if (canConfirmTimes) {
            dailyLogData[changedIndex].endTimeConfirmed = false;
          }
        } else if (canConfirmTimes) {
          dailyLogData[changedIndex].endTimeConfirmed = true;
        }

        this.archiveRecord(dailyLogData[changedIndex]);

        /* ToDO - this is causing operations to be saved twice when the user edits an end time in the daily log, and the second time
         doesn't save the correct times any more after the latest timezone changes were made. I don't know if this is actually needed,
         so commenting it out but will leave it here in case anything breaks. */
        // this.mouseUpWait(dailyLogData[changedIndex],
        //   false,
        //   component,
        //   dailyLogData[changedIndex].endTimeConfirmed);

        break;
    }

    if (changedRecord.operationInstanceId || changedRecord.operationInstanceLocalId) {
      // get operation and set operationStartTimes of each record (needs to be done so that the op start time is kept in the right offset)
      let operation = await this.operationService.getProjectSpecificOperationInstance(changedRecord.operationInstanceId || changedRecord.operationInstanceLocalId);

      if (operation) {
        dailyLogData = this.updateOperationStartTimes(dailyLogData, changedRecord.operationInstanceId
          ? changedRecord.operationInstanceId
          : changedRecord.operationInstanceLocalId, operation.startTime);
      }
    }

    // clone each operationStartTime to fix bug where next call to addSystemOffsetsToDailyLogRecords causes all operationStartTimes to get
    // added together cummutively in each loop due to some assign by reference shenanigans
    this.appData.dailyLogRecords.forEach(e => {
      e.operationStartTime = cloneDeep(e.operationStartTime) as PtLuxonDate;
    });

    const appDataClone = cloneDeep(this.appData) as AppData;

    if (!changedRecord.stepInstance) {
      // operation end changed
      const operationId = changedRecord.operationInstanceId || changedRecord.operationInstanceLocalId;
      const operationIndex = appDataClone.dailyLogOperationInstances.findIndex(e => e.id ? e.id === operationId : e.localId === operationId);

      if (operationIndex !== -1) {
        // find start record in appDataClone instead of using changedRecord, because it has the right time formats (offsets not applied to it)
        const endRecord = appDataClone.dailyLogRecords.find(e => changedRecord.id ? e.id === changedRecord.id : e.localId === changedRecord.localId);
        appDataClone.dailyLogOperationInstances[operationIndex].endTime = endRecord.endTime;
      }
    }

    appDataClone.dailyLogOperationInstances.forEach(operation => {
      this.updateOperationEndTime(operation, appDataClone.dailyLogRecords);
    });

    await this.appDataSyncService.syncLocalData(appDataClone, true, null, true);

    // not all operation records are in the appData.dailyLogRecords, and any that aren't will still have the operationStartTime in UTC,
    // so we need to update them. operationStartTime should be correct local time on changedRecord here
    if (changedRecord.operationStartTime) {
      dailyLogData = this.updateOperationStartTimes(dailyLogData, changedRecord.operationInstanceId
        ? changedRecord.operationInstanceId
        : changedRecord.operationInstanceLocalId, changedRecord.operationStartTime);
    }

    return {preventDataChange: this.preventDataChange, dailyLogData: dailyLogData};
  }

  private async updateFutureRecords(
    dailyLogData: DailyOperationLogRecord[],
    changedRecord: DailyOperationLogRecord,
    dailyLogDate: PtLuxonDate
  ): Promise<DailyOperationLogRecord[]> {
    // get future steps that need to be changed
    dailyLogData.forEach(p => {
      p.operationStartTime = PtLuxonDate.assignPtLuxonDate(p.operationStartTime);
      p.operationEndTime = p.operationEndTime != null ? PtLuxonDate.assignPtLuxonDate(p.operationEndTime) : null;
      p.startTime = PtLuxonDate.assignPtLuxonDate(p.startTime);
      p.endTime = PtLuxonDate.assignPtLuxonDate(p.endTime);
    });

    changedRecord.operationStartTime = PtLuxonDate.assignPtLuxonDate(changedRecord.operationStartTime);
    changedRecord.operationEndTime = changedRecord.operationEndTime != null ? PtLuxonDate.assignPtLuxonDate(changedRecord.operationEndTime) : null;
    changedRecord.startTime = PtLuxonDate.assignPtLuxonDate(changedRecord.startTime);
    changedRecord.endTime = PtLuxonDate.assignPtLuxonDate(changedRecord.endTime);

    const recordsToUpdate = dailyLogData
      .filter(e => this.isFutureRecord(e, changedRecord))
      .sort((a, b) => {
        if (a.sortTime < b.sortTime) {
          return -1;
        }
        if (a.sortTime > b.sortTime) {
          return 1;
        }
        return 0;
      });

    recordsToUpdate.unshift(changedRecord);

    // at this point, the changedRecord is in the recordsToUpdate array at position 0, so first recordsToUpdate[0] needs to be updated
    // but we take a copy for reference when updating the next record (it will be used to calculate next time if there are no steps)
    const changedRecordCopy = cloneDeep(recordsToUpdate[0]) as DailyOperationLogRecord;
    recordsToUpdate[0] = await this.updateChangedRecord(recordsToUpdate[0], dailyLogDate);

    // then recordsToUpdate[1..n] needs to be updated to reflect the changedRecord values
    for (let i = 1; i < recordsToUpdate.length; i++) {
      if (this.recordsUpdated === false) {
        recordsToUpdate[i] = await this.updateFutureRecord(recordsToUpdate[i], recordsToUpdate[i - 1], changedRecordCopy);
      }

      if (this.recordsUpdated) {
        recordsToUpdate[i] = await this.correctConfirmedRecordOffsets(recordsToUpdate[i]);
      }

      // if null it means the end time was invalid in regards to the start time - so prevent the change
      if (!recordsToUpdate[i] && this.preventDataChange) {
        return recordsToUpdate;
      }
    }

    await this.updateRecordOffsets(recordsToUpdate);

    // this is pointless - end of day is incorrect here
    // if (recordsToUpdate.some(e => e.sortTime > this.endOfDay.date)) {
    //   this.preventDataChange = true;
    //   return recordsToUpdate;
    // }

    if (changedRecord.operationClassification === 'Rig Move') {
      await this.updateLocationChangeRecord(changedRecord.operationInstanceId, dailyLogData);
    }

    // replace the old records in the daily log with the newly updated ones
    // this also saves these changes in local storage
    dailyLogData = this.setDailyLogData(recordsToUpdate, dailyLogData);

    return dailyLogData;
  }

  private isFutureRecord(e: DailyOperationLogRecord, changedRecord: DailyOperationLogRecord): boolean {
    const idNotNull = e.operationInstanceId != null || e.operationInstanceLocalId != null;
    const notChangedRecord = e.id !== null && e.id !== changedRecord.id || e.localId !== null && e.localId !== changedRecord.localId;
    const isEndOrStep = e.typeId === DailyLogType.Step || e.typeId === DailyLogType.OperationEnd;
    const instanceIdMatch = e.operationInstanceId
      ? e.operationInstanceId === changedRecord.operationInstanceId
      : e.operationInstanceLocalId
        ? e.operationInstanceLocalId === changedRecord.operationInstanceLocalId
        : false;

    if ((changedRecord.operationStartTime.dateTime && !e.operationStartTime.dateTime)
      || !instanceIdMatch
      || !idNotNull
      || !isEndOrStep
      || !notChangedRecord) {
      return false;
    }

    const eOperationStart = cloneDeep(e.operationStartTime.dateTime) as DateTime;
    const sortTimeBigger = e.sortTime > changedRecord.sortTime;

    const operationStartTimeBigggerWithSortTime = e.sortTime >= changedRecord.sortTime
      && eOperationStart > changedRecord.operationStartTime.dateTime;

    const operationStartTimeBigggerWithSortTimeAndOrdinal = e.sortTime >= changedRecord.sortTime
      && e.operationStartTime.dateTime >= changedRecord.operationStartTime.dateTime
      && e.ordinal > changedRecord.ordinal;

    const operationStartTimeBigggerWithSortTimeAndStepNumber = e.sortTime >= changedRecord.sortTime
      && eOperationStart >= changedRecord.operationStartTime.dateTime
      && e.ordinal >= changedRecord.ordinal && e.stepNumber > changedRecord.stepNumber;

    const operationStartTimeBiggerUniqueOrdinal = e.sortTime >= changedRecord.sortTime
      && eOperationStart >= changedRecord.operationStartTime.dateTime
      && e.ordinal >= changedRecord.ordinal
      && e.stepNumber >= changedRecord.stepNumber
      && e.uniqueOrdinal > changedRecord.uniqueOrdinal;

    const stepNumberBiggerWithSameSortTime = e.sortTime >= changedRecord.sortTime && (e.stepNumber && changedRecord.stepNumber
      ? e.stepNumber > changedRecord.stepNumber
      : false);

    const futureRecord = sortTimeBigger
        || operationStartTimeBigggerWithSortTime
        || operationStartTimeBigggerWithSortTimeAndOrdinal
        || operationStartTimeBigggerWithSortTimeAndStepNumber
        || operationStartTimeBiggerUniqueOrdinal
        || stepNumberBiggerWithSameSortTime;

    return futureRecord;
  }

  private async updateChangedRecord(record: DailyOperationLogRecord, dailyLogDate: PtLuxonDate): Promise<DailyOperationLogRecord> {
    let startEndDifference = 0;
    record.oldStartTime = cloneDeep(record.startTime) as PtLuxonDate;
    record.oldEndTime = cloneDeep(record.endTime) as PtLuxonDate;

    if (record.startTime.dateTime && record.endTime.dateTime) {
      startEndDifference = record.endTime.dateTime.toMillis() - record.startTime.dateTime.toMillis();
    }

    record = await this.setChangedRecordStartTime(record);
    record = await this.setChangedRecordEndTime(record, startEndDifference, dailyLogDate);

    if (record.endTime && record.endTime < record.startTime) {
      this.preventDataChange = true;
      return null;
    }

    record.stepInstance = this.setRecordInstance(record);

    if (await this.canConfirmTimes(record)) {
      if (this.startChanged && record.canConfirmTimes) {
        record.startTimeConfirmed = true;
      } else if (this.endChanged && record.canConfirmTimes) {
        record.endTimeConfirmed = true;
      }
    }

    record.sortTime = record.startTime.dateTime ? record.startTime.dateTime : record.endTime.dateTime;

    return record;
  }

  private async setChangedRecordStartTime(record: DailyOperationLogRecord): Promise<DailyOperationLogRecord> {
    if (!!record.startTime.dateTime) {
      this.dateService.sanitizeDate(record.startTime.dateTime);
    }

    if (record.startTimeShow != null) {
      record.startTime = await this.dateService.setPtLuxonDateFromUserEnteredString(record.startTimeShow, record.startTime.dateTime);
    } else if (record.startTime.dateTime && !record.startTimeConfirmed) {
      record.startTime = await this.dateService.setPtLuxonDateFromUserEnteredString(record.startTimeShow, record.startTime.dateTime);
      record.startTimeShow = this.dateService.setTimeStringFromPtLuxonDate(record.startTime);
    }

    return record;
  }

  private async setChangedRecordEndTime(record: DailyOperationLogRecord, startEndDifference: number, dailyLogDate: PtLuxonDate): Promise<DailyOperationLogRecord> {
    if (this.startChanged && !record.endTimeConfirmed && record.endTime.dateTime && record.startTime.dateTime) {
      record.endTime.dateTime = await this.dateService.addDuration(record.startTime.dateTime,{millisecond: startEndDifference});
      record.endTimeShow = this.dateService.setTimeStringFromPtLuxonDate(record.endTime);
    } else if (this.endChanged && !record.endTimeConfirmed && record.endTime.dateTime) {
      record.endTime = await this.dateService.setPtLuxonDateFromUserEnteredString(record.endTimeShow, record.endTime.dateTime);
    } else if (record.endTimeConfirmed && record.endTime.dateTime) {
      // if end is confirmed, we need to call this setPtLuxonDateFromUserEnteredString just to ensure that the offsets are in sync with other times
      record.endTime = await this.dateService.setPtLuxonDateFromUserEnteredString(record.endTimeShow, record.endTime.dateTime);
    }

    // if adding end time back after having previously removed it
    if (!record.endTime.dateTime && (record.endTimeShow !== '' && record.endTimeShow != null)) {
      record.endTime = await this.dateService.setPtLuxonDateFromUserEnteredString(record.endTimeShow, record.endTime.dateTime || dailyLogDate.dateTime);
    }

    this.recordsUpdated = record.endTimeConfirmed;

    if ((record.endTimeShow === null || record.endTimeShow === '') && record.typeId !== DailyLogType.OperationStart) {
      this.endTimeRemoved = true;
      await this.dateChangeRecordService.removeTypeOfRecord(record.startTime, DailyLogType.DateChange);
      await this.dateChangeRecordService.removeTypeOfRecord(record.startTime, DailyLogType.Continued);
    }

    return record;
  }

  private setRecordInstance(record: DailyOperationLogRecord): StepInstance {
    if (record.stepInstance) {
      record.stepInstance.sortTime = record.sortTime;

      if (record.startTime) {
        record.stepInstance.startTime = record.startTime;
      }

      if (record.endTime) {
        record.stepInstance.endTime = record.endTime;
      }

      return record.stepInstance;
    }

    return null;
  }

  private async updateFutureRecord(
    record: DailyOperationLogRecord,
    previousRecord: DailyOperationLogRecord,
    changedRecordCopy: DailyOperationLogRecord): Promise<DailyOperationLogRecord> {
    if (record.startTimeConfirmed) {
      this.recordsUpdated = true;
      return record;
    }

    if (this.endTimeRemoved === true) {
      record = this.archiveRecord(record);
      return record;
    }

    // make sure to calculate this, and set old times before making any changes to times
    record.oldStartTime = cloneDeep(record.startTime) as PtLuxonDate;
    record.oldEndTime = cloneDeep(record.endTime) as PtLuxonDate;

    const startEndDifference = await this.getStartEndDifference(record, previousRecord);

    record.archived = false;
    record = await this.setFutureRecordStartTime(record, previousRecord);

    if (!record.endTimeConfirmed) {
      record = await this.setFutureRecordEndTime(record, previousRecord, startEndDifference);
    } else {
      this.recordsUpdated = true;
    }

    if (record.endTime && record.endTime.dateTime < record.startTime.dateTime) {
      this.preventDataChange = true;
      return null;
    }

    record.sortTime = record.startTime.dateTime ? record.startTime.dateTime : record.endTime.dateTime;
    record.stepInstance = this.setRecordInstance(record);

    if (!!record.stepInstance && record.stepInstance.exclude) {
      record.archived = true;
    }

    return record;
  }

  // get the original difference between two records, so we know what's needed to add onto a future record
  // previousRecord times will be updated here, but the original times are stored in oldStartTime/oldEndTime
  private async getStartEndDifference(record: DailyOperationLogRecord, previousRecord: DailyOperationLogRecord) {
    let startEndDifference = 0;

    if (record.startTime.dateTime && record.endTime.dateTime) {
      startEndDifference = record.endTime.dateTime.toMillis() - record.startTime.dateTime.toMillis();
    } else if (record.endTime.dateTime && !record.startTime.dateTime && previousRecord.endTime.dateTime) {
      // if editing operation end time of an operation that has steps
      startEndDifference = record.endTime.dateTime.toMillis() - (previousRecord.oldEndTime && previousRecord.oldEndTime.dateTime
        ? previousRecord.oldEndTime.dateTime.toMillis()
        : previousRecord.oldStartTime.dateTime.toMillis());
    } else if (record.endTime.dateTime && !record.startTime.dateTime && previousRecord.startTime.dateTime) {
      // editing end time of an operation with no steps - i.e record = end record and previousRecord = start record
      startEndDifference = record.endTime.dateTime.toMillis() - previousRecord.oldStartTime.dateTime.toMillis();
    }

    return startEndDifference;
  }

  private async setFutureRecordStartTime(
    record: DailyOperationLogRecord,
    previousRecord: DailyOperationLogRecord
  ): Promise<DailyOperationLogRecord> {
    if (record.stepInstance && record.startTime.dateTime == null && previousRecord.endTime.dateTime !== null) {
      record.startTime = await this.dateService.setPtLuxonDateFromUserEnteredString(previousRecord.endTimeShow, previousRecord.endTime.dateTime);
      record.startTime.dateTime = await this.dateService.addDuration(record.startTime.dateTime, {seconds: record.stepInstance.secondsDelay});
    } else if (record.startTime.dateTime) {
      record.startTime.dateTime = previousRecord.endTime.dateTime
        ? previousRecord.endTime.dateTime
        : previousRecord.startTime.dateTime;
      record.startTime.dateTime = await this.dateService.addDuration(record.startTime.dateTime,{second: record.stepInstance.secondsDelay});
    }

    record.startTimeShow = this.dateService.setTimeStringFromPtLuxonDate(record.startTime);

    return record;
  }

  private async setFutureRecordEndTime(
    record: DailyOperationLogRecord,
    previousRecord: DailyOperationLogRecord,
    startEndDifference: number
  ): Promise<DailyOperationLogRecord> {

    if (record.startTime.dateTime && record.endTime.dateTime) {
      record.endTime.dateTime = await this.dateService.addDuration(record.startTime.dateTime,{millisecond: startEndDifference});
    } else if (record.startTime.dateTime) {
      record.endTime = cloneDeep(record.startTime) as PtLuxonDate; // set the date to be equal to start time
      record.endTime.dateTime = await this.dateService.addDuration(record.startTime.dateTime,{seconds: record.stepInstance.secondsDelay});
    } else if (!record.startTime.dateTime) {
      // check for previousRecord endtime, if editing an operation start time that has no steps, then previousRecord end will be null here
      if (previousRecord.endTime.dateTime) {
        record.endTime.dateTime = await this.dateService.addDuration(previousRecord.endTime.dateTime,{millisecond: startEndDifference});
      } else {
        record.endTime.dateTime = await this.dateService.addDuration(previousRecord.startTime.dateTime,{millisecond: startEndDifference});
      }
    }

    record.endTimeShow = this.dateService.setTimeStringFromPtLuxonDate(record.endTime);

    return record;
  }

  async correctConfirmedRecordOffsets(record: DailyOperationLogRecord): Promise<DailyOperationLogRecord> {
    if (record.startTime.dateTime) {
      record.startTime = await this.dateService.setPtLuxonDateFromUserEnteredString(record.startTimeShow, record.startTime.dateTime);
    }

    if (record.endTime.dateTime) {
      record.endTime = await this.dateService.setPtLuxonDateFromUserEnteredString(record.endTimeShow, record.endTime.dateTime);
    }

    record.sortTime = record.startTime.dateTime ? record.startTime.dateTime : record.endTime.dateTime;

    return record;
  }

  private archiveRecord(record: DailyOperationLogRecord): DailyOperationLogRecord {
    // code block that handles cases where the user is removing an end time
    if (this.endTimeRemoved === true) {
      if (record.startTimeConfirmed === true || record.endTimeConfirmed === true) {
        this.recordsUpdated = true;
        return record;
      } else if (record.endTimeConfirmed === false) {
        record.archived = true;
        record.startTime.setDate(null);
        record.startTimeShow = null;
        record.endTime.setDate(null);
        record.endTimeShow = null;
        return record;
      }
    }

    return record;
  }

  private setDailyLogData(updatedRecords: DailyOperationLogRecord[], dailyLogData: DailyOperationLogRecord[]): DailyOperationLogRecord[] {
    updatedRecords.forEach(record => {
      const index = dailyLogData.findIndex(e => record.id ? e.id === record.id : e.localId === record.localId);
      dailyLogData[index] = record;

      // this.dailyLogService.saveDailyLogData([dailyLogData[index]]);
      this.appData.dailyLogRecords.push(dailyLogData[index]);
    });

    return dailyLogData;
  }

  private async updateRecordOperation(recordsToUpdate: DailyOperationLogRecord[]) {
    const records = cloneDeep(recordsToUpdate) as DailyOperationLogRecord[];
    const instances = await this.recordOperationLinkService.updateOperations(records);
    for (let i = 0; i < instances.length; i++) {
      const index = this.appData.dailyLogOperationInstances
        .findIndex(e => instances[i].id
          ? e.id === instances[i].id
          : e.localId === instances[i].localId);

      if (index !== -1) {
        this.appData.dailyLogOperationInstances[index] = instances[i];
      } else {
        this.appData.dailyLogOperationInstances.push(instances[i]);
      }
    }
  }


  async timeChanged(date: DateTime, enteredTime: string): Promise<boolean> {
    const enteredPtDate = await this.dateService.setPtLuxonDateFromUserEnteredString(enteredTime, date);

    if (date != null && enteredPtDate != null && enteredPtDate.dateTime && enteredPtDate.dateTime.toMillis() !== date.toMillis()) {
      return true;
    } else if (date == null && enteredPtDate != null && enteredPtDate.dateTime != null || date != null && (enteredPtDate == null || enteredPtDate.dateTime == null)) {
      return true;
    }

    return false;
  }

  async updateTransitingRecord(record: DailyOperationLogRecord, dailyLogData: DailyOperationLogRecord[]): Promise<boolean> {
    const index = dailyLogData.findIndex(e =>
      e.operationInstanceId === record.operationInstanceId
      && e.typeId === DailyLogType.Transiting);

    if (index !== -1) {
      const transitingRecord = cloneDeep(dailyLogData[index]) as DailyOperationLogRecord;

      // update dailyLogData array time, this is what will be returned to the daily log
      dailyLogData[index].startTimeShow = cloneDeep(record.startTimeShow);
      dailyLogData[index].startTime = cloneDeep(record.startTime) as PtLuxonDate;
      dailyLogData[index].sortTime = cloneDeep(record.startTime.dateTime) as DateTime;

      transitingRecord.startTimeShow = cloneDeep(record.startTimeShow);

      await this.updateRecordOperation([transitingRecord]);
      await this.dailyLogService.saveDailyLogData([transitingRecord]);

      // add transit daily log record to appData
      const transitRecordToPush = cloneDeep(dailyLogData[index]) as DailyOperationLogRecord;
      this.appData.dailyLogRecords.push(transitRecordToPush);

      // update transit setup change record
      const setupData = await this.setupService.getSetupChangeLogData();
      const savedTransitRecord = setupData.find(e => e.changeTypeId === 6 && e.dailyLogRecordId === dailyLogData[index].id);
      if (savedTransitRecord) {
        savedTransitRecord.timeOfChange = cloneDeep(dailyLogData[index].startTime) as PtLuxonDate;
        savedTransitRecord.timeOfChangeToShow = cloneDeep(dailyLogData[index].startTimeShow);
        this.appData.setupChangeLogRecords.push(savedTransitRecord);
      }

      return true;
    }

    return false;
  }

  /* Called when a rig move operation gets changed in the daily log - as it needs to update any location change linked with that move */
  async updateLocationChangeRecord(psoId: number, dailyLogData: DailyOperationLogRecord[], existingDateChanged?: PtLuxonDate) {
    const index = dailyLogData.findIndex(e =>
      e.operationInstanceId === psoId
      && e.typeId === DailyLogType.LocationChange);
    const operationEndRecord = dailyLogData.find(e =>
      e.operationInstanceId === psoId
      && e.typeId === DailyLogType.OperationEnd);

    if (index !== -1) {
      if (operationEndRecord.endTime.dateTime === null || operationEndRecord.endTime.dateTime === undefined) {
        dailyLogData[index].archived = true;
        this.dailyLogService.saveDailyLogData([dailyLogData[index]]);
        return;
      }

      dailyLogData[index].startTimeShow = operationEndRecord.endTimeShow;
      dailyLogData[index].startTime = await this.dateService.setPtLuxonDateFromUserEnteredString(operationEndRecord.endTimeShow, operationEndRecord.startTime.dateTime || operationEndRecord.endTime.dateTime);
      dailyLogData[index].sortTime = operationEndRecord.endTime.dateTime;
      dailyLogData[index].operationStartTime = operationEndRecord.operationStartTime;
      dailyLogData[index].operationEndTime = operationEndRecord.operationEndTime;
      dailyLogData[index].archived = false;

      await this.updateRecordOperation([dailyLogData[index]]);
      await this.dailyLogService.saveDailyLogData([dailyLogData[index]]);
      this.appData.dailyLogRecords.push(dailyLogData[index]);

      this.setupService.getSetupChangeLogData().then(async (data) => {
        const setupChange = data.find(e => e.changeTypeId === 4 && e.dailyLogRecordId === dailyLogData[index].id);
        if (setupChange) {
          setupChange.timeOfChange = cloneDeep(operationEndRecord.endTime) as PtLuxonDate;
          setupChange.timeOfChangeToShow = operationEndRecord.endTimeShow;
          this.appData.setupChangeLogRecords.push(setupChange);
        }
      });
    }
  }

  private resetVariables() {
    this.recordsUpdated = false;
    this.endTimeRemoved = false;
    this.startChanged = false;
    this.endChanged = false;
    this.preventDataChange = false;

    this.appData = new AppData();
    this.appData.dailyLogRecords = [];
    this.appData.setupChangeLogRecords = [];
    this.appData.dailyLogOperationInstances = [];
  }

  public async updateRecordOffsets(dailyLogData: DailyOperationLogRecord[]) {
    for (const record of dailyLogData) {
      const startTimeZone = await this.timeZoneService.getEditorLocally(record.startTime) as TimeZoneChangeEditor;
      if (startTimeZone) {
        record.startTimeOffset = startTimeZone.secondsOffsetFromUtc / 60 / 60;
      }

      const endTimeZone = await this.timeZoneService.getEditorLocally(record.endTime);
      if (endTimeZone) {
        record.endTimeOffset = endTimeZone.secondsOffsetFromUtc / 60 / 60;
      }
    }
  }

  async updateOperationConcurrency(dailyLogData: DailyOperationLogRecord[], instanceId: number) {
    const instance = await this.operationService.getProjectSpecificOperationInstance(instanceId);
    const affectedOperationData = dailyLogData.filter(p => p.operationInstanceId === instanceId);
    let changesMade = false;

    affectedOperationData.forEach(log => {
      const isContractual = !this.operationService.isTypeAtTimeConcurrent(instance, log.startTime.dateTime);
      if (isContractual !== log.isContractual) {
        log.isContractual = isContractual;
        changesMade = true;
      }
    });

    if (changesMade) {
      this.dailyLogService.saveDailyLogData(affectedOperationData);
    }
  }

  isSetupChange(type: DailyLogType) {
    if (type === DailyLogType.LocationChange
      || type === DailyLogType.StageChange
      || type === DailyLogType.PhaseChange
      || type === DailyLogType.VesselMasterChanged
      || type === DailyLogType.OtherSetupChange
      || type === DailyLogType.ProjectChange) {
      return true;
    }

    return false;
  }

  // This is needed to ensure that when the user clicks a confirm checkbox in the daily log, while their caret is inside that row's input,
  // that it doesn't immediately unconfirm itself after being confirmed (previously the input blur() would confirm/unconfirm the time, and
  // then clicking on the checkbox would cancel out whatever just happened in the input blur(), so we needed to add some form of timeout
  mouseUpWait(row: DailyOperationLogRecord, isStart: boolean, component: DailyLogComponent, endConfirmedStatus?: boolean) {
    if (!row) {
      return;
    }

    if (endConfirmedStatus == null) {
      endConfirmedStatus = false;
    }

    const source = interval(100);
    if (this.mouseUpSubscribe) {
      this.mouseUpSubscribe.unsubscribe();
    }

    this.mouseUpSubscribe = source.subscribe(async () => {
      if (!this.lastTimeConfirmedFlag && this.timeConfirmedMouseUpFlag) {
        if (await this.canConfirmTimes(row)) {
          if (isStart) {
            row.startTimeConfirmed = true;
          } else {
            row.endTimeConfirmed = endConfirmedStatus;
          }
        }
        this.dailyLogService.saveDailyLogData([row]);
        this.recordOperationLinkService.updateOperations([row]);

        const appData = new AppData();
        appData.dailyLogRecords = [row];
        this.operationService.getOperationsFromLogs([row])
          .then(res => {
            appData.dailyLogOperationInstances = res;
            component.$confirmedChange.next(appData);
            component.validate();
          });

        this.mouseUpSubscribe.unsubscribe();
      }
    });
  }

  public async canConfirmTimes(record: DailyOperationLogRecord, operationInstances?: DailyLogOperationInstance[]) {
    if (record.lineTypeName === 'Operation Start' || record.lineTypeName === 'Operation End') {
      // Get operation and check subclassification
      const operation = await this.getOperationInstanceForRecord(record, operationInstances);

      if (!operation) {
        return;
      }

      return operation.projectSpecificOperation.operationSummary.subClassificationName !== 'Jacking';
    } else if (record.lineTypeName === 'Step') {
      // If not a system default - you can confirm times
      if (!record.stepInstance.step || !record.stepInstance.step.isSystemDefault) {
        return true;
      } else {
        // Disable if Jacking
        const operation = await this.getOperationInstanceForRecord(record, operationInstances);

        if (!operation) {
          return;
        }

        return operation.projectSpecificOperation.operationSummary.subClassificationName !== 'Jacking';
      }
    } else {
      return true;
    }
  }

  private async getOperationInstanceForRecord(record: DailyOperationLogRecord, operationInstances: DailyLogOperationInstance[]): Promise<DailyLogOperationInstance> {
    if (operationInstances && operationInstances.length > 0) {
      return operationInstances.find(e => e.id == record.operationInstanceId);
    }

    return await this.operationService.getProjectSpecificOperationInstance(
      record.operationInstanceId);
  }

  private updateOperationStartTimes(dailyLogData: DailyOperationLogRecord[], operationId: number, newStartTime: PtLuxonDate) {
    if (!newStartTime || !newStartTime.dateTime) {
      return dailyLogData;
    }

    for (let i = 0; i < dailyLogData.length; i++) {
      if (dailyLogData[i].operationInstanceId ? dailyLogData[i].operationInstanceId === operationId : dailyLogData[i].operationInstanceLocalId === operationId) {
        dailyLogData[i].operationStartTime = newStartTime;
      }
    }

    const filteredOps = this.appData.dailyLogOperationInstances
      .filter(e => e.id ? e.id === operationId : e.localId === operationId);
    if (filteredOps && filteredOps.length > 0) {
      filteredOps.forEach(e => {
        e.startTime = newStartTime;
      });
    }

    return dailyLogData;
  }

  private updateOperationEndTime(operation: DailyLogOperationInstance, dailyLogData: DailyOperationLogRecord[]): DailyLogOperationInstance {
    const filteredOpRecords = dailyLogData
      .filter(e =>
        (e.operationInstanceId || e.operationInstanceLocalId)
        && (operation.id
        ? e.operationInstanceId === operation.id
        : e.operationInstanceLocalId === e.operationInstanceId)
        && (e.typeId === 3 || e.type === DailyLogType.OperationEnd.toString()));

    if (filteredOpRecords && filteredOpRecords.length === 1) {
      const endRecord = filteredOpRecords[0]; // should only be 1 if the above filter works correctly
      operation.endTime = endRecord.endTime;
    }

    return operation;
  }
}



// WEBPACK FOOTER //
// ./src/services/daily-log/daily-log-time/daily-log-time-service.ts